/**
 * /auth/* endpoints — public auth + JWT issuance, plus signed-in 2FA management.
 * Mostly mutations (login, signup, 2FA setup/enable/disable); the one query is
 * the 2FA status read for the profile (useTwoFactorStatus).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@clarix/api-client';
import { useAuthStore, loginUrlForSlug } from '@clarix/auth-store';
import type {
  SignupRequest,
  SignupResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  LoginRequest,
  LoginResponse,
  LoginResult,
  RequestLoginOtpRequest,
  RequestLoginOtpResponse,
  LoginWithOtpRequest,
  VerifyLoginTwoFactorRequest,
  TwoFactorSetupResponse,
  EnableTwoFactorRequest,
  EnableTwoFactorResponse,
  DisableTwoFactorRequest,
  TwoFactorStatusResponse,
  CompleteEnrolmentRequest,
  ChangePasswordRequest,
  SwitchWorkspaceRequest,
  SwitchWorkspaceResponse,
  JwtPayload,
} from '@clarix/shared-types';
import {
  isEnrol2faRequired,
  isPasswordChangeRequired,
  isPasswordResetRequired,
  isTwoFactorChallenge,
} from '@clarix/shared-types';
import { unwrap, type ApiResponse } from './envelope';
import { queryKeys } from './keys';

/** No-verify decode used purely for UI hints (workspace_slug, app_access). */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function persistSession(token: string): void {
  // A full session means any pending force-reset is resolved — drop the sticky
  // flag so RequireAuth stops pinning the user to /password-reset-required.
  sessionStorage.removeItem('clarix.pwreset.required');
  const claims = decodeJwtPayload(token);
  useAuthStore.getState().setSession({
    token,
    user: claims,
    workspaceSlug: claims?.workspace_slug ?? null,
  });
}

/** A full session — none of the gated login variants (2FA / enrolment / policy). */
function isFullSession(result: LoginResult): result is LoginResponse {
  return (
    !isTwoFactorChallenge(result) &&
    !isEnrol2faRequired(result) &&
    !isPasswordChangeRequired(result) &&
    !isPasswordResetRequired(result)
  );
}

/**
 * Persist ONLY a genuine full session; return the raw result unchanged so the
 * login UI can route the gated variants (2FA challenge, forced-2FA-enrolment,
 * forced password change — see LoginResult) to their follow-up steps.
 *
 * The gated variants must NOT be persisted here: a challenge carries no token,
 * and the enrolment / password-change tokens are LIMITED (no workspace access).
 * Persisting a limited token would trip RequireUnauthed and bounce the user off
 * /login mid-flow, so the gate steps carry those tokens in component state and
 * send them per-request instead (see `bearer`).
 */
function persistIfFullSession(result: LoginResult): LoginResult {
  if (isFullSession(result)) persistSession(result.access_token);
  return result;
}

/**
 * Per-request Authorization for the gated follow-up endpoints (2FA setup/enable,
 * complete-enrolment, change-password). These run while the user is still on
 * /login with an empty auth store, so the request interceptor won't overwrite
 * this header (it only sets one when the store holds a token).
 */
function bearer(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

/**
 * Cross-subdomain auth handoff via URL hash.
 *
 * Why: Chrome silently drops cookies scoped to `Domain=.localhost`, so the
 * cookie set by the gateway on apex (localhost:5100) is not visible to JS on
 * any other *.localhost subdomain. We hand the token over in a URL hash
 * instead — invisible to the server, not logged, removed from the URL after
 * consumption.
 *
 * Producer side (LoginPage, OnboardingPage):
 *   window.location.replace(`http://acme.localhost:5000/#token=${jwt}`)
 *
 * Consumer side (this function, called from main.tsx at app boot):
 *   reads location.hash, populates auth-store, strips the hash from the URL.
 *
 * In production under HTTPS we can switch to SameSite=None;Secure cookies and
 * drop this hash hop — backend already sets the cookie unchanged either way.
 */
export const HASH_TOKEN_PARAM = 'token';

export function bootstrapAuthFromHash(): void {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  if (!hash) return;
  // Accept both `#token=...` and `#foo=bar&token=...`
  const match = hash.match(new RegExp(`[#&]${HASH_TOKEN_PARAM}=([^&]+)`));
  if (!match) return;
  const token = decodeURIComponent(match[1] ?? '');
  if (!token) return;
  persistSession(token);
  // Strip just the token param; preserve any other hash fragments.
  const remaining = hash
    .slice(1)
    .split('&')
    .filter((kv) => !kv.startsWith(`${HASH_TOKEN_PARAM}=`))
    .join('&');
  const newHash = remaining ? `#${remaining}` : '';
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search + newHash,
  );
}

export function useSignup() {
  return useMutation({
    mutationFn: async (body: SignupRequest): Promise<SignupResponse> => {
      const res = await getApiClient().post<ApiResponse<SignupResponse>>('/auth/signup', body);
      return unwrap(res.data);
    },
  });
}

/**
 * POST /auth/verify-otp — NOT a session. Returns a short-lived
 * email_verification_token proving the email was OTP-verified; nothing is
 * persisted here. Carry the token into the workspace-creation step.
 */
export function useVerifyOtp() {
  return useMutation({
    mutationFn: async (body: VerifyOtpRequest): Promise<VerifyOtpResponse> => {
      const res = await getApiClient().post<ApiResponse<VerifyOtpResponse>>(
        '/auth/verify-otp',
        body,
      );
      return unwrap(res.data);
    },
  });
}

export function useResendOtp() {
  return useMutation({
    mutationFn: async (body: {
      email: string;
      purpose?: 'SIGNUP' | 'PASSWORD_RESET';
    }): Promise<void> => {
      const res = await getApiClient().post<ApiResponse<unknown>>('/auth/resend-otp', body);
      unwrap(res.data);
    },
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async (body: LoginRequest): Promise<LoginResult> => {
      const res = await getApiClient().post<ApiResponse<LoginResult>>('/auth/login', body);
      return persistIfFullSession(unwrap(res.data));
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      try {
        await getApiClient().post<ApiResponse<unknown>>('/auth/logout');
      } catch {
        // Even if the server-side revoke fails, clear local state.
      }
      // Read the slug BEFORE clear() wipes it (single-host keeps it only here).
      const slug = useAuthStore.getState().workspaceSlug;
      useAuthStore.getState().clear();
      qc.clear();
      // Stay on THIS origin: a workspace subdomain signs out to its own
      // /login, where LoginRoute reads the slug from the hostname and renders
      // the sign-in form. Hopping to the apex would discard the slug and send
      // the user back through the workspace picker to reach the workspace they
      // were just in. window.location (not react-router) because this must be a
      // hard navigation — it drops in-memory app state on the way out.
      //
      // No `?force-logout=1`: that hint exists to wipe a DIFFERENT origin's
      // stale localStorage, and same-origin storage was already cleared above.
      //
      // Single-host has no subdomain to hold the slug, so it rides along in the
      // query and main.tsx adopts it back — otherwise sign-out would land on the
      // workspace picker. (mirrors packages/layout useSignOut)
      window.location.replace(loginUrlForSlug(slug));
    },
  });
}

/**
 * POST /auth/request-login-otp — passwordless login: sends a 6-digit code to
 * the user's email. Same success response regardless of whether the email
 * exists (prevents account enumeration).
 */
export function useRequestLoginOtp() {
  return useMutation({
    mutationFn: async (body: RequestLoginOtpRequest): Promise<RequestLoginOtpResponse> => {
      const res = await getApiClient().post<ApiResponse<RequestLoginOtpResponse>>(
        '/auth/request-login-otp',
        body,
      );
      return unwrap(res.data);
    },
  });
}

/**
 * POST /auth/login-with-otp — verify the code, mint a JWT, persist the session
 * the same way the password login does. Returns the same shape as /auth/login.
 */
export function useLoginWithOtp() {
  return useMutation({
    mutationFn: async (body: LoginWithOtpRequest): Promise<LoginResult> => {
      const res = await getApiClient().post<ApiResponse<LoginResult>>(
        '/auth/login-with-otp',
        body,
      );
      return persistIfFullSession(unwrap(res.data));
    },
  });
}

/**
 * POST /auth/2fa/verify-login — redeem a 2FA login challenge (issued by /login or
 * /login-with-otp for a 2FA-enrolled account). Public: the challenge_token is the
 * credential. Returns a full session, OR a forced password-change gate (a stale
 * password is enforced AFTER 2FA so it never bypasses the second factor).
 */
export function useVerifyLoginTwoFactor() {
  return useMutation({
    mutationFn: async (body: VerifyLoginTwoFactorRequest): Promise<LoginResult> => {
      const res = await getApiClient().post<ApiResponse<LoginResult>>(
        '/auth/2fa/verify-login',
        body,
      );
      return persistIfFullSession(unwrap(res.data));
    },
  });
}

/**
 * POST /auth/2fa/setup — begin enrolment; returns the shared secret and an otpauth
 * URL (rendered as a QR). `token` is the LIMITED enrolment token for forced login
 * enrolment (passed per-request, never persisted); omit it when a full session is
 * already in the store (voluntary enable from the profile) — the interceptor
 * attaches that token automatically.
 */
export function useSetupTwoFactor() {
  return useMutation({
    mutationFn: async (token?: string): Promise<TwoFactorSetupResponse> => {
      const res = await getApiClient().post<ApiResponse<TwoFactorSetupResponse>>(
        '/auth/2fa/setup',
        undefined,
        token ? bearer(token) : undefined,
      );
      return unwrap(res.data);
    },
  });
}

/**
 * POST /auth/2fa/enable — confirm a TOTP code; returns one-time backup codes.
 * `token` is optional for the same reason as useSetupTwoFactor.
 */
export function useEnableTwoFactor() {
  return useMutation({
    mutationFn: async ({
      token,
      code,
    }: EnableTwoFactorRequest & { token?: string }): Promise<EnableTwoFactorResponse> => {
      const res = await getApiClient().post<ApiResponse<EnableTwoFactorResponse>>(
        '/auth/2fa/enable',
        { code } satisfies EnableTwoFactorRequest,
        token ? bearer(token) : undefined,
      );
      return unwrap(res.data);
    },
  });
}

/**
 * GET /auth/2fa/status — whether 2FA is enabled for the signed-in user, plus how
 * many backup codes remain. Uses the persisted session (profile context).
 */
export function useTwoFactorStatus() {
  return useQuery({
    queryKey: queryKeys.twoFactorStatus(),
    queryFn: async (): Promise<TwoFactorStatusResponse> => {
      const res = await getApiClient().get<ApiResponse<TwoFactorStatusResponse>>('/auth/2fa/status');
      return unwrap(res.data);
    },
  });
}

/** POST /auth/2fa/disable — turn 2FA off; requires a current authenticator code. */
export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DisableTwoFactorRequest): Promise<{ disabled: boolean }> => {
      const res = await getApiClient().post<ApiResponse<{ disabled: boolean }>>(
        '/auth/2fa/disable',
        body,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.twoFactorStatus() });
    },
  });
}

/**
 * Both enrolment-gate exits redeem the SAME limited token for a full session and
 * persist it — only the endpoint differs. One factory so the two can never drift
 * in how the token is carried (per-request `bearer`, never the store) or how the
 * resulting session is persisted.
 */
function useRedeemEnrolmentToken(path: '/auth/2fa/complete-enrolment' | '/auth/2fa/skip-enrolment') {
  return useMutation({
    mutationFn: async ({
      token,
      workspaceSlug,
    }: {
      token: string;
      workspaceSlug?: string | null;
    }): Promise<LoginResponse> => {
      const body: CompleteEnrolmentRequest = workspaceSlug ? { workspace_slug: workspaceSlug } : {};
      const res = await getApiClient().post<ApiResponse<LoginResponse>>(path, body, bearer(token));
      const data = unwrap(res.data);
      persistSession(data.access_token);
      return data;
    },
  });
}

/**
 * POST /auth/2fa/complete-enrolment — swap the LIMITED enrolment token for a full
 * session once 2FA is enabled. Returns a full session, which is persisted here.
 */
export function useCompleteEnrolment() {
  return useRedeemEnrolmentToken('/auth/2fa/complete-enrolment');
}

/**
 * POST /auth/2fa/skip-enrolment — dismiss the OPTIONAL one-time "set up 2FA?"
 * offer and swap the LIMITED enrolment token for a full session. Marks the user
 * as prompted so it is never shown again.
 *
 * Only legal when the gate carried `can_skip: true`; the server re-checks the
 * policy and returns 401 when 2FA is actually compulsory, so a client that offers
 * the button in the wrong state fails safely rather than bypassing the gate.
 */
export function useSkipEnrolment() {
  return useRedeemEnrolmentToken('/auth/2fa/skip-enrolment');
}

/**
 * POST /auth/change-password — force a password change on a login gated by a stale
 * password. Authed by the LIMITED change token (client-only, per-request — never
 * in the JSON body). On success the token is spent: the user re-signs-in.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async ({
      token,
      ...body
    }: ChangePasswordRequest & { token: string }): Promise<{ changed: boolean }> => {
      const res = await getApiClient().post<ApiResponse<{ changed: boolean }>>(
        '/auth/change-password',
        body,
        bearer(token),
      );
      return unwrap(res.data);
    },
  });
}

export function useSwitchWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SwitchWorkspaceRequest): Promise<SwitchWorkspaceResponse> => {
      const res = await getApiClient().post<ApiResponse<SwitchWorkspaceResponse>>(
        '/auth/switch-workspace',
        body,
      );
      const data = unwrap(res.data);
      persistSession(data.access_token);
      qc.invalidateQueries({ queryKey: queryKeys.me() });
      return data;
    },
  });
}

// ─── Forgot password (logged-out recovery) ──────────────────────────────────
// Three steps, all public (the gateway allowlists them — a user who has
// forgotten their password cannot hold a JWT):
//   1. useRequestPasswordReset  email → a 6-digit code is mailed
//   2. useVerifyResetOtp        code  → a single-use reset_token
//   3. useResetPassword         token + new password → done, then sign in
// None of them mints a session: the user signs in normally afterwards, so 2FA
// and the password-policy gate still run.

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (body: { email: string }): Promise<void> => {
      const res = await getApiClient().post<ApiResponse<unknown>>('/auth/forgot-password', body);
      unwrap(res.data);
    },
  });
}

export function useVerifyResetOtp() {
  return useMutation({
    mutationFn: async (body: { email: string; code: string }): Promise<string> => {
      const res = await getApiClient().post<ApiResponse<{ reset_token: string }>>(
        '/auth/verify-reset-otp',
        body,
      );
      return unwrap(res.data).reset_token;
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (body: {
      reset_token: string;
      new_password: string;
    }): Promise<{ reset: boolean }> => {
      const res = await getApiClient().post<ApiResponse<{ reset: boolean }>>(
        '/auth/reset-password',
        body,
      );
      return unwrap(res.data);
    },
  });
}
